import {MessageTick} from './message-ticks';
import type {ConversationListFilter} from '../components/conversation-list/conversation-list.component';

/**
 * THE CONVERSATION LIST'S WORDING (ITP-E-203-01), after carbon's Chat panel (OCA-E-160, `ChatPanel.tsx` rows
 * 2521-2600): the stamp on a row's first line, what its second line leads with, and the empty copy. Pure, so the row
 * template only draws what these decide.
 */

const pad = (value: number): string => String(value).padStart(2, '0');

/**
 * A row's stamp: `HH:mm` (24h) when the last message was sent on the reader's local today, `dd/mm/yy` before that, and
 * nothing when there is no usable date. Both are all-digit and fixed width, so the column of stamps never jogs.
 */
export function rowStamp(iso: string | Date | null | undefined, now: Date = new Date()): string {
  if (!iso) {
    return '';
  }
  const at = new Date(iso);
  if (isNaN(at.getTime())) {
    return '';
  }
  const today = at.getFullYear() === now.getFullYear() && at.getMonth() === now.getMonth() && at.getDate() === now.getDate();
  return today
    ? `${pad(at.getHours())}:${pad(at.getMinutes())}`
    : `${pad(at.getDate())}/${pad(at.getMonth() + 1)}/${pad(at.getFullYear() % 100)}`;
}

/**
 * A message's HTML as the one plain line a row shows: tags dropped, line and paragraph breaks read as spaces, runs of
 * white space collapsed. Carbon's preview is plain text for the same reason: the line truncates and cannot carry markup.
 */
export function previewText(html: string | null | undefined): string {
  if (!html) {
    return '';
  }
  const spaced = html.replace(/<br\s*\/?>|<\/(p|div|li)>/gi, ' ');
  const text = new DOMParser().parseFromString(spaced, 'text/html').body.textContent || '';
  return text.replace(/\s+/g, ' ').trim();
}

/** The server's `card.via` on a meeting card posted for a pasted Orbitax Meet link (ITP-E-208-06). */
export const CARD_VIA_LINK = 'link';

/**
 * What a card row's starter did (B-20): started a meeting from the chat (ITP-E-208-01), shared one by pasting its Orbitax
 * Meet link (ITP-E-208-06, `card.via` "link") or scheduled one from the chat (ITP-E-208-04). Show and mission cards
 * (Release 2) say nothing.
 */
type CardDeed = 'started' | 'shared' | 'scheduled';

/** The list preview's words for each deed: the line has no glyph, so it says "video meeting". */
const CARD_PREVIEW_VERBS: Readonly<Record<CardDeed, string>> = {
  started: 'started a video meeting',
  shared: 'shared a video meeting',
  scheduled: 'scheduled a meeting'
};

/** The card header's words for each deed (ITP-E-240-06): the card's glyph and title say the rest. */
const CARD_HEADER_VERBS: Readonly<Record<CardDeed, string>> = {
  started: 'started a meeting',
  shared: 'shared a meeting',
  scheduled: 'scheduled a meeting'
};

/**
 * Who did what on a card row, the one rule the list preview and the card header both word: the deed from the card's kind
 * and `via`, and its doer, "You" on the reader's own, else the sender's first and last name ('' when they have none).
 * Null for any other message, or a deleted one.
 */
function cardDeed(message: any): {doer: string, deed: CardDeed} | null {
  const kind = message?.card?.kind;
  const deed: CardDeed | null = kind === 'meeting' ? (message.card.via === CARD_VIA_LINK ? 'shared' : 'started')
    : kind === 'event' ? 'scheduled' : null;
  if (!deed || message.isDeleted) {
    return null;
  }
  const doer = message.isMine ? 'You'
    : [message.createdBy?.firstName, message.createdBy?.lastName].filter(Boolean).join(' ');
  return {doer, deed};
}

/**
 * The preview of a card row (B-20, ITP-E-208-02): a meeting started from the chat reads "<Name> started a video meeting",
 * or "You started a video meeting" on the reader's own, whatever the stored fallback says; a meeting scheduled from it
 * (ITP-E-208-04) reads "<Name> scheduled a meeting" or "You scheduled a meeting"; the card a pasted Meet link brought
 * (ITP-E-208-06, `card.via` "link") reads "<Name> shared a video meeting" or "You shared a video meeting". Null for any
 * other message.
 */
export function cardPreview(message: any): string | null {
  const said = cardDeed(message);
  if (!said) {
    return null;
  }
  return said.doer ? `${said.doer} ${CARD_PREVIEW_VERBS[said.deed]}` : previewText(message.message);
}

/**
 * The header line of a card row's card in the thread (ITP-E-240-06), where the card stands alone with no bubble or
 * sentence: "<Name> started a meeting", "<Name> shared a meeting" (a pasted link's card), "<Name> scheduled a meeting",
 * and "You ..." on the reader's own. A sender with no name is named by their email. Null for any other message, a Show or
 * mission card, or nobody to name.
 */
export function cardHeader(message: any): string | null {
  const said = cardDeed(message);
  const doer = said?.doer || message?.createdBy?.email;
  return said && doer ? `${doer} ${CARD_HEADER_VERBS[said.deed]}` : null;
}

/** What a row knows about itself, read by the list from the conversation and the messenger ports. */
export interface ChatRowFacts {
  /** The reader's unsent draft, as one line; '' when there is none. */
  draft: string;
  /** The last event is a call row. */
  isCall: boolean;
  /** That call was missed by the reader. */
  missedCall: boolean;
  /** The tick of the last message when it is the reader's own (ITP-E-194-08), else null. */
  tick: MessageTick | null;
  /** Somebody mentioned the reader and it is unread (B-02). */
  mention: boolean;
  unreadCount: number;
  /** The reader marked it unread by hand (B-08). */
  markedUnread: boolean;
  /** Pinned (B-05) and muted (B-06), as the organisation port says. */
  pinned: boolean;
  muted: boolean;
}

/** The flags that decide whether the pin and mute marks may show at all. */
export interface ChatRowFlags {
  pin: boolean;
  mute: boolean;
}

/**
 * The row's second line. `lead` is the one thing it opens with, in carbon's priority: the reader's draft, then a
 * missed call, then any other call, then the tick on the reader's own message, else plain text. The marks after it are
 * the `@` pill and the count; a muted row's count is quiet unless it holds a mention, and a row marked unread with no
 * count shows a bare disc.
 */
export interface ChatLineTwo {
  lead: 'draft' | 'missed' | 'call' | 'tick' | 'text';
  tick: MessageTick | null;
  mention: boolean;
  count: number;
  dot: boolean;
  quiet: boolean;
  pinned: boolean;
  muted: boolean;
}

export function lineTwo(row: ChatRowFacts, flags: ChatRowFlags): ChatLineTwo {
  const lead: ChatLineTwo['lead'] = row.draft ? 'draft'
    : row.isCall && row.missedCall ? 'missed'
      : row.isCall ? 'call'
        : row.tick ? 'tick' : 'text';
  const count = row.unreadCount > 0 ? row.unreadCount : 0;
  const muted = flags.mute && row.muted;
  return {
    lead,
    tick: lead === 'tick' ? row.tick : null,
    mention: row.mention,
    count,
    dot: !count && row.markedUnread,
    quiet: muted && !row.mention,
    pinned: flags.pin && row.pinned,
    muted
  };
}

/**
 * The list's empty copy: carbon's, for the All chip and for any narrower view, the Archived place included. The
 * Messages page says "Nothing here." for every view, as carbon's /meet/messages does (page.tsx:498-499).
 */
export function emptyCopy(filter: ConversationListFilter, archived = false, onPage = false): string {
  return filter === 'all' && !archived && !onPage ? 'No conversations.' : 'Nothing here.';
}
